import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import ReassuranceBand from "@/components/ReassuranceBand";
import { ArrowRight, ShieldCheck, TrendingUp, ScrollText, Home as HomeIcon, Users } from "lucide-react";

export default function Home() {
  const [listings, setListings] = useState([]);
  useEffect(() => {
    api.get("/listings?limit=3").then(({ data }) => setListings(data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO title="venteaterme.fr — Vente à terme immobilière" description="Vendez sans aléa, achetez sans banque. Plateforme spécialisée en vente à terme immobilière : bouquet + mensualités fixes sur durée déterminée. Alternative moderne au viager." />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/8143683/pexels-photo-8143683.jpeg?auto=compress&cs=tinysrgb&h=900" alt="" width="1600" height="900" fetchPriority="high" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A41]/85 via-[#1B2A41]/70 to-[#1B2A41]/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 w-full">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-6">Vente à terme immobilière</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.05] tracking-tight mb-8">
              Vendez sans aléa,<br /><span className="text-accent-brand">achetez sans banque.</span>
            </h1>
            <p className="text-lg md:text-xl text-white max-w-2xl mb-6 leading-relaxed" style={{textShadow: "0 2px 8px rgba(0,0,0,0.5)"}}>
              Un achat sans crédit bancaire, sans intérêts et sans indexation&nbsp;: un <strong className="text-accent-brand">prix ferme</strong> payé de façon échelonnée. L&rsquo;alternative moderne au viager, sans aléa de durée de vie.
            </p>
            <p className="text-sm text-white max-w-2xl mb-4 uppercase tracking-widest font-semibold" style={{textShadow: "0 2px 6px rgba(0,0,0,0.6)"}}>Prix ferme · Mensualités fixes · Sans intérêts · Sans indexation</p>
            <p className="text-sm text-white max-w-2xl mb-10 font-semibold" style={{textShadow: "0 2px 6px rgba(0,0,0,0.6)"}}>
              <span className="inline-block bg-accent-brand text-white text-xs uppercase tracking-widest font-bold px-3 py-1.5 mr-3">100&nbsp;% gratuit</span>
              Aucune commission, aucun honoraire — vendeurs, acheteurs et agences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/simulateur" data-testid="hero-cta-simulate"
                className="bg-accent-brand text-white px-8 py-4 font-bold tracking-wide hover:bg-[#B08D22] transition-colors inline-flex items-center justify-center gap-2">
                Simuler mon opération <ArrowRight size={18} />
              </Link>
              <Link to="/annonces" data-testid="hero-cta-listings"
                className="border-2 border-white/70 text-white px-8 py-4 font-bold tracking-wide hover:bg-white hover:text-navy transition-colors inline-flex items-center justify-center">
                Voir les annonces
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BANDEAU RÉASSURANCE */}
      <ReassuranceBand />

      {/* 3 ÉTAPES */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <Reveal className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-accent-brand font-bold mb-3">Le principe</div>
            <h2 className="font-serif text-3xl md:text-5xl text-navy tracking-tight leading-tight text-balance">Trois piliers, une opération sécurisée.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              { icon: HomeIcon, tag: "01", title: "Le bouquet", text: "Un capital initial versé à la signature de l'acte notarié — entre 10 % et 40 % du prix de vente. L'acheteur devient immédiatement propriétaire." },
              { icon: TrendingUp, tag: "02", title: "Les mensualités fixes", text: "Un paiement échelonné mensuel, sur une durée fixée d'avance (5 à 30 ans). Aucune banque, aucun intérêt, aucune indexation. Le prix est ferme et connu." },
              { icon: ScrollText, tag: "03", title: "Fiscalité avantageuse", text: "Les mensualités perçues par le vendeur ne sont pas imposables. Plus-value souvent exonérée (résidence principale, durée de détention)." },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 90} className="bg-white border border-navy/10 p-8 md:p-10 card-lift">
                <div className="flex items-start justify-between mb-8">
                  <s.icon className="text-accent-brand" size={36} strokeWidth={1.4} />
                  <div className="font-serif text-4xl text-navy/15 tabular-nums">{s.tag}</div>
                </div>
                <h3 className="font-serif text-2xl text-navy mb-3">{s.title}</h3>
                <p className="text-sm text-navy/70 leading-relaxed">{s.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ANNONCES */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex items-end justify-between mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-accent-brand font-bold mb-3">À la une</div>
              <h2 className="font-serif text-3xl md:text-5xl text-navy tracking-tight">Dernières annonces</h2>
            </div>
            <Link to="/annonces" data-testid="home-annonces-link" className="hidden md:inline-flex items-center gap-2 text-navy hover:text-accent-brand text-sm font-bold uppercase tracking-wider">
              Tout voir <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {listings.map((l, i) => (
              <Reveal key={l.id} delay={i * 80}>
                <ListingCard l={l} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VENDEURS VS ACHETEURS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid md:grid-cols-2 gap-8">
          {[
            { icon: Users, tag: "Vendeurs", title: "Transformer votre patrimoine en revenus", bullets: [
              "Bouquet immédiat pour un projet ou une épargne", "Mensualités fixes non imposables pendant la durée", "Protection : hypothèque légale spéciale (art. 2402 du Code civil) + clause résolutoire",
              "Vos héritiers protégés : les mensualités continuent en cas de décès"] },
            { icon: HomeIcon, tag: "Acheteurs", title: "Devenir propriétaire sans passer par la banque", bullets: [
              "Aucun dossier bancaire, aucun taux de crédit", "Propriétaire dès la signature de l'acte", "Prix fixé à la signature, sans révision", "Une vraie alternative au crédit immobilier classique"] },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 120} as="div" className={`p-10 md:p-14 border card-lift ${i === 0 ? "bg-navy text-cream border-navy" : "bg-white border-navy/10"}`}>
              <div className="flex items-center gap-3 mb-8">
                <c.icon className={i === 0 ? "text-accent-brand" : "text-accent-brand"} size={28} strokeWidth={1.4} />
                <div className={`text-xs uppercase tracking-[0.2em] font-bold ${i === 0 ? "text-accent-brand" : "text-accent-brand"}`}>{c.tag}</div>
              </div>
              <h3 className={`font-serif text-3xl mb-8 leading-tight ${i === 0 ? "text-cream" : "text-navy"}`}>{c.title}</h3>
              <ul className="space-y-3">
                {c.bullets.map((b, j) => (
                  <li key={j} className={`flex items-start gap-3 text-sm ${i === 0 ? "text-cream/85" : "text-navy/75"}`}>
                    <ShieldCheck size={16} className="text-accent-brand mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-24 md:py-32 bg-navy text-cream">
        <div className="max-w-4xl mx-auto text-center px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-4">Simulez en 30 secondes</div>
          <h2 className="font-serif text-4xl md:text-6xl text-cream leading-tight mb-6">Combien pouvez-vous obtenir&nbsp;?</h2>
          <p className="text-cream/75 max-w-2xl mx-auto mb-10 text-lg">Notre simulateur calcule instantanément votre bouquet et vos mensualités en fonction de la valeur du bien, du type et de la durée.</p>
          <Link to="/simulateur" data-testid="home-final-cta"
            className="inline-flex items-center gap-2 bg-accent-brand text-white px-10 py-4 font-bold tracking-wide hover:bg-[#B08D22] transition-colors">
            Lancer le simulateur <ArrowRight size={18} />
          </Link>
          <p className="text-xs text-cream/50 mt-8 max-w-xl mx-auto">Estimation indicative ne constituant pas un conseil juridique ou fiscal personnalisé.</p>
        </div>
      </section>
    </>
  );
}
